// One vocabulary for a visit's status, shared by the visitor list cards, the Home feed,
// the details sheet, the ring popup and the in-app banner (see the approved
// "Visitor Flows" spec). A walk-in is someone the guard registered at the gate
// (source GUARD); an invite is a pass the resident created in advance.
import { COLORS, GREY, SLATE } from '../styles/theme';

export type VisitorTone = 'wait' | 'exp' | 'in' | 'left' | 'den' | 'ok' | 'can';

export const VISITOR_TONES: Record<VisitorTone, { color: string; bg: string }> = {
    wait: { color: COLORS.warningText, bg: COLORS.warningBg },
    exp:  { color: COLORS.info, bg: COLORS.infoBg },
    // No blue token in the theme yet; Tailwind blue-700/100, as the app already uses inline.
    in:   { color: '#1d4ed8', bg: '#dbeafe' },
    left: { color: SLATE[600], bg: SLATE[200] },
    den:  { color: COLORS.errorDark, bg: COLORS.errorBg },
    ok:   { color: COLORS.success, bg: COLORS.successBg },
    can:  { color: GREY[500], bg: GREY[100] },
};

export const VISITOR_TONE_ICONS: Record<VisitorTone, string> = {
    wait: '⏳', exp: '🗓', in: '→', left: '←', den: '✕', ok: '✓', can: '⊘',
};

export interface VisitorStatusView {
    label: string;
    tone: VisitorTone;
    icon: string;
    color: string;
    bg: string;
}

export const isWalkIn = (source?: string | null): boolean => source === 'GUARD';

const view = (label: string, tone: VisitorTone): VisitorStatusView => ({
    label, tone, icon: VISITOR_TONE_ICONS[tone], ...VISITOR_TONES[tone],
});

/** Label, tone and icon for a visit's status. `source` is the visit's source ('GUARD' = walk-in). */
export const getVisitorStatusView = (status?: string | null, source?: string | null): VisitorStatusView => {
    const s = String(status || '').toUpperCase();
    const walkIn = isWalkIn(source);
    switch (s) {
        case 'COMING':
        case 'PENDING':
            return walkIn ? view('Waiting at gate', 'wait') : view('Expected', 'exp');
        case 'ENTERED': return view('Inside', 'in');
        case 'EXITED': return view('Left', 'left');
        case 'REJECTED': return walkIn ? view('Denied', 'den') : view('Cancelled', 'can');
        case 'DELETED':
        case 'CANCELLED':
            return view('Cancelled', 'can');
        case 'APPROVED':
        case 'GUARD':
            return view('Approved · not in yet', 'ok');
        default:
            return view(s ? s.charAt(0) + s.slice(1).toLowerCase() : 'Unknown', 'can');
    }
};

export type VisitorStepState = 'done' | 'bad' | 'now' | 'todo';

export interface VisitorStep {
    label: string;
    state: VisitorStepState;
    /** Shown under the label for done/bad steps only. */
    note?: string;
    icon: string;
}

export interface VisitorStepInput {
    status?: string | null;
    source?: string | null;
    enteredAt?: number | string | null;
    exitedAt?: number | string | null;
    createdAt?: number | string | null;
    approvedByName?: string | null;
}

export const formatVisitTime = (value?: number | string | null): string => {
    if (value === null || value === undefined || value === '') return '';
    const d = new Date(typeof value === 'number' ? value : (/^\d+$/.test(String(value)) ? Number(value) : value));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Progress steps per source. Walk-in: At gate → Approved (or Denied) → Inside → Left.
 * Invite: Invited → Inside → Left. Done steps carry their time; future steps are plain.
 */
export const getVisitorSteps = (v: VisitorStepInput): VisitorStep[] => {
    const s = String(v.status || '').toUpperCase();
    // createdAt = when the guard registered it / the resident invited (new backend);
    // enteredAt = real entry time (null until entered). Old backends sent no createdAt and
    // put the creation time in enteredAt, so fall back to it.
    const createdT = formatVisitTime(v.createdAt ?? v.enteredAt);
    const inT = formatVisitTime(v.enteredAt);
    const outT = formatVisitTime(v.exitedAt);
    const by = v.approvedByName || '';
    const isIn = s === 'ENTERED' || s === 'EXITED';
    const left: VisitorStep = s === 'EXITED'
        ? { label: 'Left', state: 'done', note: outT, icon: '←' }
        : { label: 'Left', state: 'todo', icon: '←' };

    if (isWalkIn(v.source)) {
        const atGate: VisitorStep = { label: 'At gate', state: 'done', note: createdT, icon: '✓' };
        if (s === 'REJECTED') return [atGate, { label: 'Denied', state: 'bad', note: by, icon: '✕' }];
        if (s === 'DELETED' || s === 'CANCELLED') return [atGate, { label: 'Cancelled', state: 'bad', icon: '⊘' }];
        if (s === 'COMING' || s === 'PENDING') {
            return [atGate, { label: 'Approve?', state: 'now', icon: '⏳' },
                { label: 'Inside', state: 'todo', icon: '→' }, left];
        }
        return [
            atGate,
            { label: 'Approved', state: 'done', note: by, icon: '✓' },
            isIn ? { label: 'Inside', state: 'done', note: inT, icon: '→' } : { label: 'Inside', state: 'todo', icon: '→' },
            left,
        ];
    }

    const invited: VisitorStep = { label: 'Invited', state: 'done', note: v.createdAt != null ? formatVisitTime(v.createdAt) : '', icon: '✓' };
    if (s === 'REJECTED' || s === 'DELETED' || s === 'CANCELLED') {
        return [invited, { label: 'Cancelled', state: 'bad', icon: '⊘' }];
    }
    return [
        invited,
        isIn ? { label: 'Inside', state: 'done', note: inT, icon: '→' }
            : { label: 'Inside', state: (s === 'COMING' || s === 'PENDING') ? 'now' : 'todo', icon: '→' },
        left,
    ];
};

/** "A-101" from a visit's wing/home, or '' when the DTO doesn't carry them. */
export const flatLabel = (v?: { wingName?: string | null; homeNo?: string | null } | null): string => {
    const wing = (v?.wingName || '').toString().trim();
    const home = (v?.homeNo || '').toString().trim();
    if (wing && home) return `${wing}-${home}`;
    return home || wing;
};

/** "In 11:53 · out 12:40 (47 min)". */
export const inOutText = (enteredAt?: number | string | null, exitedAt?: number | string | null): string => {
    const inT = formatVisitTime(enteredAt);
    const outT = formatVisitTime(exitedAt);
    if (!inT && !outT) return '';
    if (!outT) return `In ${inT}`;
    const a = Number(enteredAt), b = Number(exitedAt);
    const mins = Number.isFinite(a) && Number.isFinite(b) && b > a ? Math.round((b - a) / 60000) : null;
    return `In ${inT} · out ${outT}${mins !== null ? ` (${mins} min)` : ''}`;
};
